import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { RoadmapGrid } from "./components/RoadmapGrid";
import { VojomoSymbol } from "./components/VojomoSymbol";
import { Button } from "./components/ui/button";
import type { AllBuildingBlocks } from "./types";
import { Loader2, ExternalLink } from "lucide-react";
import "./rb-globals.css";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface SharedRoadmap {
  companyName: string;
  draftVersion: number;
  publishedAt: string;
  publishedBlocks: AllBuildingBlocks;
  sessionId: string;
}

export function RoadmapBuilderShareView() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedRoadmap | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const r = await fetch(`${SERVER_URL}/api/rb/share/${token}`);
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.error || "Failed to load roadmap");
        }
        setData(await r.json());
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load roadmap");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [token]);

  if (isLoading) {
    return (
      <div className="rb-root h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading roadmap...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rb-root h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-sm px-6">
          <VojomoSymbol className="h-10 w-10 mx-auto mb-4 opacity-40" />
          <p className="text-lg font-semibold mb-1">Roadmap not found</p>
          <p className="text-sm text-muted-foreground mb-4">
            {error ||
              "This share link may have been revoked or doesn't exist."}
          </p>
          <Link to="/roadmap/build_roadmap">
            <Button variant="outline" size="sm">
              Go to Roadmap Builder
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const publishedDate = new Date(data.publishedAt).toLocaleDateString(
    "en-US",
    { month: "long", day: "numeric", year: "numeric" }
  );

  return (
    <div className="rb-root h-screen flex flex-col bg-background">
      <header className="flex items-center justify-between gap-2 px-4 py-2.5 border-b bg-background/80 backdrop-blur-md z-40 shrink-0">
        <div className="flex items-center gap-3">
          <VojomoSymbol className="h-5 w-5" />
          <div>
            <span className="font-semibold text-sm">{data.companyName}</span>
            <span className="text-xs text-muted-foreground ml-2">
              v{data.draftVersion} · Published {publishedDate}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/roadmap/build_roadmap/${data.sessionId}`}>
            <Button variant="outline" size="sm" className="text-xs h-8 gap-1.5">
              <ExternalLink className="h-3.5 w-3.5" />
              Open in app
            </Button>
          </Link>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto max-w-3xl w-full mx-auto px-2">
        <RoadmapGrid
          buildingBlocks={data.publishedBlocks}
          companyName={data.companyName}
          sessionId={data.sessionId}
        />
      </div>
    </div>
  );
}
